export type ActivityType = 'call' | 'sms' | 'email' | 'event' | 'note'
export type SmsDirection = 'inbound' | 'outbound'
export type CallDisposition =
  | 'Connected'
  | 'Voicemail'
  | 'No Answer'
  | 'Busy'
  | 'Left Message'
export type EmailStatus = 'sent' | 'opened' | 'bounced' | 'clicked'

export interface Activity {
  id: string
  type: ActivityType
  timestamp: string // ISO string
  summary: string
  details?: string

  // Call specific
  duration?: number // in seconds
  disposition?: CallDisposition
  isMeaningful?: boolean

  // Email specific
  emailStatus?: EmailStatus

  // SMS specific
  smsDirection?: SmsDirection

  // Event specific
  eventType?: string

  // Note specific
  author?: string
}

export interface BorrowerMetrics {
  lastMeaningfulContact: string | null // ISO string
  lastCallDisposition: CallDisposition | null
  lastCallDuration: number | null // in seconds
  emailsSent: number
  emailsOpened: number
}

// Helper to format dates for mock data relative to today
const today = new Date()
const subtractDays = (days: number, hours: number = 0, minutes: number = 0) => {
  const d = new Date(today)
  d.setDate(d.getDate() - days)
  d.setHours(d.getHours() - hours)
  d.setMinutes(d.getMinutes() - minutes)
  return d.toISOString()
}

export const mockActivities: Activity[] = [
  {
    id: 'act-1',
    type: 'call',
    timestamp: subtractDays(0, 2, 15), // 2 hours 15 mins ago
    summary: 'Outbound Call - Follow up on W-2 submission',
    details:
      'Spoke with Sarah regarding her 2023 W-2s. She mentioned she had trouble downloading them from her employer portal but will try again tonight. Walked her through the alternative document upload process just in case. Very engaged and eager to close.',
    duration: 752, // 12m 32s
    disposition: 'Connected',
    isMeaningful: true,
  },
  {
    id: 'act-2',
    type: 'note',
    timestamp: subtractDays(0, 4, 0), // 4 hours ago
    summary: 'Internal Note: Rate Lock Expiring Soon',
    details:
      "Sarah's current rate lock of 6.25% expires in 12 days. We need to ensure all conditions are cleared by next Friday to avoid extension fees.",
    author: 'Michael Chang (Processor)',
  },
  {
    id: 'act-3',
    type: 'email',
    timestamp: subtractDays(1, 5, 30), // 1 day ago
    summary: 'Action Required: Upload Missing Income Documents',
    details:
      'Hi Sarah, We are reviewing your file and noticed we are still missing your 2023 W-2s. Please upload them to the secure portal at your earliest convenience to keep your loan on track for the target closing date.',
    emailStatus: 'opened',
  },
  // --- SMS conversation: recent burst (~1 day ago, all within ~15 min) ---
  {
    id: 'act-sms-1',
    type: 'sms',
    timestamp: subtractDays(1, 3, 0), // ~1 day ago
    summary: 'Got it, uploading tonight! Thanks for the heads up 😊',
    smsDirection: 'inbound',
  },
  {
    id: 'act-sms-2',
    type: 'sms',
    timestamp: subtractDays(1, 3, 4), // 4 min earlier
    summary:
      'Perfect — just tap the W-2 line item and attach the PDF. Let me know if the portal gives you any trouble.',
    smsDirection: 'outbound',
  },
  {
    id: 'act-sms-3',
    type: 'sms',
    timestamp: subtractDays(1, 3, 9), // 5 min earlier
    summary: 'Do I upload both pages or just the first one?',
    smsDirection: 'inbound',
  },
  {
    id: 'act-sms-4',
    type: 'sms',
    timestamp: subtractDays(1, 3, 12), // 3 min earlier
    summary: 'Both pages please, so we have the full document on file.',
    smsDirection: 'outbound',
  },
  {
    id: 'act-sms-5',
    type: 'sms',
    timestamp: subtractDays(1, 6, 0), // ~1 day ago (start of the burst)
    summary:
      'Hi Sarah! Quick reminder to upload your 2023 W-2s to the secure portal when you get a chance.',
    smsDirection: 'outbound',
  },
  // --- SMS conversation: earlier standalone thread (~4 days ago) ---
  {
    id: 'act-sms-6',
    type: 'sms',
    timestamp: subtractDays(4, 1, 0), // ~4 days ago
    summary: 'Sounds good, talk then!',
    smsDirection: 'inbound',
  },
  {
    id: 'act-sms-7',
    type: 'sms',
    timestamp: subtractDays(4, 1, 6), // 6 min earlier
    summary:
      "Great! I'll give you a call tomorrow at 2pm to walk through the disclosures.",
    smsDirection: 'outbound',
  },
  {
    id: 'act-4',
    type: 'call',
    timestamp: subtractDays(2, 1, 10), // 2 days ago
    summary: 'Outbound Call - Document Check-in',
    details:
      'Left a voicemail reminding Sarah about the pending W-2s and bank statements.',
    duration: 45, // 45s
    disposition: 'Voicemail',
    isMeaningful: false,
  },
  {
    id: 'act-5',
    type: 'event',
    timestamp: subtractDays(3, 0, 0), // 3 days ago
    summary: 'Appraisal Scheduled',
    details:
      'Appraisal scheduled for the subject property at 123 Oak Street. Appraiser: John Doe Appraisals LLC. Expected turnaround time is 5-7 business days.',
    eventType: 'Property Appraisal',
  },
  {
    id: 'act-6',
    type: 'email',
    timestamp: subtractDays(4, 2, 0), // 4 days ago
    summary: 'Initial Disclosures Sent for eSign',
    details:
      'Your initial loan disclosures have been generated and sent via DocuSign. Please review and sign within 48 hours to proceed with your application.',
    emailStatus: 'opened',
  },
  {
    id: 'act-7',
    type: 'call',
    timestamp: subtractDays(5, 3, 45), // 5 days ago
    summary: 'Initial Consultation & Pre-Approval',
    details:
      'Great initial conversation. Sarah is a first-time homebuyer looking in the Austin area. Pre-approved for $450k conventional, 20% down. Discussed rate options and monthly payment comfort levels.',
    duration: 1845, // 30m 45s
    disposition: 'Connected',
    isMeaningful: true,
  },
]

export const mockMetrics: BorrowerMetrics = {
  lastMeaningfulContact: mockActivities[0].timestamp,
  lastCallDisposition: 'Connected',
  lastCallDuration: 752,
  emailsSent: 2,
  emailsOpened: 2,
}

// Conversation participants
export const INTERNAL_USER_NAME = 'Michael Chang'
export const CLIENT_NAME = 'Sarah Mitchell'

export interface SmsThread {
  id: string
  messages: Activity[] // ordered oldest -> newest
  start: string // ISO timestamp of first message
  end: string // ISO timestamp of last message
}

// Group SMS activities into conversation threads. Consecutive messages whose
// gap is within `gapMinutes` are threaded together into a single conversation.
export function groupSmsIntoThreads(
  smsActivities: Activity[],
  gapMinutes = 60,
): SmsThread[] {
  // Sort oldest -> newest so we can walk the conversation in order
  const sorted = [...smsActivities].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  )
  const gapMs = gapMinutes * 60 * 1000
  const threads: SmsThread[] = []
  let current: Activity[] = []
  for (const msg of sorted) {
    if (current.length === 0) {
      current = [msg]
      continue
    }
    const prev = current[current.length - 1]
    const delta =
      new Date(msg.timestamp).getTime() - new Date(prev.timestamp).getTime()
    if (delta <= gapMs) {
      current.push(msg)
    } else {
      threads.push(makeThread(current))
      current = [msg]
    }
  }
  if (current.length > 0) threads.push(makeThread(current))
  // Newest conversation first
  return threads.sort(
    (a, b) => new Date(b.end).getTime() - new Date(a.end).getTime(),
  )
}

function makeThread(messages: Activity[]): SmsThread {
  return {
    id: `thread-${messages[0].id}`,
    messages,
    start: messages[0].timestamp,
    end: messages[messages.length - 1].timestamp,
  }
}
